import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import {
  applyTheme,
  DEFAULT_THEME,
  getTheme,
  isValidThemeId,
  ThemeDefinition,
  ThemeId,
  themes,
  THEME_IDS,
} from '../styles/themes';

const STORAGE_KEY = 'theme';
const SELECTED_KEY = 'hasSelectedTheme';

interface ThemeContextType {
  themeId: ThemeId;
  currentTheme: ThemeDefinition;
  availableThemes: ThemeDefinition[];
  hasSelectedTheme: boolean;
  showThemeSelector: boolean;
  showWelcomeModal: boolean;
  setTheme: (id: ThemeId, persist?: boolean) => void;
  previewTheme: (id: ThemeId) => void;
  markThemeAsSelected: () => void;
  setShowThemeSelector: (show: boolean) => void;
  setShowWelcomeModal: (show: boolean) => void;
  syncThemeFromUser: (userTheme?: string | null) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeId, setThemeId] = useState<ThemeId>(DEFAULT_THEME);
  const [hasSelectedTheme, setHasSelectedTheme] = useState(true);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    const selected = localStorage.getItem(SELECTED_KEY) === 'true';
    const id = saved && isValidThemeId(saved) ? saved : DEFAULT_THEME;
    setThemeId(id);
    applyTheme(id);
    setHasSelectedTheme(selected);
    if (!selected) {
      const t = window.setTimeout(() => setShowWelcomeModal(true), 800);
      return () => window.clearTimeout(t);
    }
  }, []);

  const persistTheme = useCallback(async (id: ThemeId) => {
    localStorage.setItem(STORAGE_KEY, id);
    try {
      await api.updateThemePreference(id);
    } catch {
      /* offline or not logged in */
    }
  }, []);

  const setTheme = useCallback(
    (id: ThemeId, persist = true) => {
      if (!THEME_IDS.includes(id)) return;
      setThemeId(id);
      applyTheme(id);
      if (persist) persistTheme(id);
    },
    [persistTheme]
  );

  const previewTheme = useCallback((id: ThemeId) => {
    if (!THEME_IDS.includes(id)) return;
    applyTheme(id);
    setThemeId(id);
  }, []);

  const markThemeAsSelected = useCallback(() => {
    setHasSelectedTheme(true);
    localStorage.setItem(SELECTED_KEY, 'true');
    localStorage.setItem(STORAGE_KEY, themeId);
    persistTheme(themeId);
    setShowWelcomeModal(false);
  }, [themeId, persistTheme]);

  const syncThemeFromUser = useCallback(
    (userTheme?: string | null) => {
      if (userTheme && isValidThemeId(userTheme)) {
        setThemeId(userTheme);
        applyTheme(userTheme);
        localStorage.setItem(STORAGE_KEY, userTheme);
      }
    },
    []
  );

  const value = useMemo(
    () => ({
      themeId,
      currentTheme: getTheme(themeId),
      availableThemes: Object.values(themes),
      hasSelectedTheme,
      showThemeSelector,
      showWelcomeModal,
      setTheme,
      previewTheme,
      markThemeAsSelected,
      setShowThemeSelector,
      setShowWelcomeModal,
      syncThemeFromUser,
    }),
    [
      themeId,
      hasSelectedTheme,
      showThemeSelector,
      showWelcomeModal,
      setTheme,
      previewTheme,
      markThemeAsSelected,
      syncThemeFromUser,
    ]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
